import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';

// MSwipe Interfaces
export interface MSwipeTokenResponse {
  status: string | boolean;
  msg: string;
  token: string;
}

export interface MSwipePaymentRequest {
  amount: string;
  mobileno: string;
  custcode: string;
  user_id: string;
  sessiontoken: string;
  versionno: string;
  imeino: string;
  email_id: string;
  invoice_id: string;
  request_id: string;
  redirect_url: string;
  device_id: string;
  addlnote1: string;
  addlnote2: string;
  addlnote3: string;
  addlnote4: string;
  addlnote5: string;
  addlnote6: string;
  addlnote7: string;
  addlnote8: string;
  addlnote9: string;
  addlnote10: string;
  LinkValidity: string;
  paymentreason: string;
  ApplicationId: string;
  ChannelId: string;
  ClientId: string;
}

export interface MSwipePaymentResponse {
  txn_id: string;
  status: string;
  smslink: string;
  MessageContent: string;
  ExtraNote1: string;
  ExtraNote2: string;
  ExtraNote3: string;
  ExtraNote4: string;
  ExtraNote5: string;
  responsecode: string;
  responsemessage: string;
}

export interface MSwipeTransactionStatusRequest {
  id: string;
}

export interface MSwipeTransactionStatusResponse {
  Status: string;
  ResponseCode: string;
  ResponseMessage: string;
  Data: MSwipeTransactionData[];
}

export interface MSwipeTransactionData {
  IPG_ID: string;
  Amount: number;
  Cust_Code: string;
  UserId: string;
  MID: string;
  AmexMID: string;
  TID: string;
  Mobile_No: string;
  EmailId: string;
  city: string;
  countrycode: string;
  statecode: string;
  Payment_Status: number;
  validity: boolean;
  valid_till_date: string;
  Order_Id: string;
  Payment_Id: string;
  Payment_Url: string;
  Created_On: string;
  Inv_Url: string;
  PIN: string;
  Cust_PinCode: string;
  IntlCard_Allow: string;
  Mcc_Code: string;
  Merchant_Type: string;
  isAmexEnabled: boolean;
  isNBEnabled: boolean;
  IPG_Type: string;
  CallBackURL: string;
  Full_Name_reqd: boolean;
  MobileNo_reqd: boolean;
  EmailId_reqd: boolean;
  BillingAddress_reqd: boolean;
  ShippingAddress_reqd: boolean;
  Instructions_reqd: boolean;
  DeliveryDateTime_reqd: boolean;
  SellerID: string;
  IsEMIEnabled: boolean;
  Emi_Type: string;
  TokenId: string;
  OfferId: string;
  Item_code: string;
  Supplier_Code: string;
  Item_OEM_Code: string;
  Cust_Location_Code: string;
  ValidateSrNoRequired: string;
  Item_Lot_Type: string;
  CRN_No: string;
  Parent_IPGId: string;
  PaymentReason: string;
  IsCardEnabled: boolean;
  pblauth: string;
}

@Injectable({
  providedIn: 'root'
})
export class MswipeService {
  private backendUrl = 'https://stories.funtelstudio.com/storyapi/mswipe_handler.php';
  private currentEnvironment: 'uat' | 'prod' = 'prod'; // ✅ DEFAULT PRODUCTION
  private currentToken = new BehaviorSubject<string>('');
  
  constructor(private http: HttpClient) { 
    console.log('🚀 MSwipe Service initialized with:', this.currentEnvironment);
  }

  setEnvironment(environment: 'uat' | 'prod') {
    this.currentEnvironment = environment;
    this.currentToken.next('');
    console.log('🔧 Environment changed to:', this.currentEnvironment);
  }

  getCurrentEnvironment(): 'uat' | 'prod' {
    console.log('🔧 Current Environment:', this.currentEnvironment);
    return this.currentEnvironment;
  }
  // Generate token through backend proxy
  generateToken(): Observable<MSwipeTokenResponse> {
    return this.http.post<MSwipeTokenResponse>(this.backendUrl, {
      action: 'generate_token',
      environment: this.currentEnvironment
    }, { withCredentials: true });
  }

  // Initiate payment through backend proxy
  initiatePayment(paymentData: Partial<MSwipePaymentRequest>): Observable<MSwipePaymentResponse> {
    const requestData = {
      action: 'initiate_payment',
      environment: this.currentEnvironment,
      amount: paymentData.amount || "0.00",
      user_id: paymentData.user_id || "",
      email_id: paymentData.email_id || "",
      invoice_id: paymentData.invoice_id || `INV${Date.now()}`,
      request_id: paymentData.request_id || `REQ${Date.now()}`,
      redirect_url: paymentData.redirect_url || "https://stories.funtelstudio.com/payment-success",
      paymentreason: paymentData.paymentreason || "Wallet Recharge",
      mSwipeToken: paymentData.sessiontoken || this.currentToken.value // ✅ Name change
    };

    return this.http.post<MSwipePaymentResponse>(this.backendUrl, requestData, { withCredentials: true });
  }

  // Get transaction status through backend proxy
  getTransactionStatus(transactionId: string): Observable<MSwipeTransactionStatusResponse> {
    return this.http.post<MSwipeTransactionStatusResponse>(this.backendUrl, {
      action: 'get_transaction_status',
      environment: this.currentEnvironment,
      transaction_id: transactionId
    }, { withCredentials: true });
  }

  setToken(token: string): void {
    this.currentToken.next(token);
  }

  getToken(): string {
    return this.currentToken.value;
  }

  extractTransIdFromLink(smslink: string): string {
    try {
      const url = new URL(smslink);
      const transId = url.searchParams.get('TransID');
      return transId || '';
    } catch (error) {
      console.error('Error extracting TransID:', error);
      return '';
    }
  }

  storeMSwipeResponse(transactionId: string, mswipeResponse: MSwipePaymentResponse): Observable<any> {
    return this.http.post('https://stories.funtelstudio.com/storyapi/store_mswipe_response.php', {
      transaction_id: transactionId,
      mswipe_response: mswipeResponse,
      environment: this.currentEnvironment
    }, { withCredentials: true });
  }

  getMSwipeResponse(transactionId: string): Observable<any> {
    return this.http.get(`https://stories.funtelstudio.com/storyapi/get_mswipe_response.php?transaction_id=${transactionId}`, {
      withCredentials: true
    });
  }
}